/* eslint-disable max-lines */
import { GetActiveSessionResponse } from '@wix/ambassador-chatbot-v1-session/types';
import {
  Article,
  ArticleResolution,
  ArticleType,
  Category,
  Label,
} from '@wix/answers-api';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { isProduction } from '@wix/wix-run-mode';
import cheerio from 'cheerio';
import classNames from 'classnames';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { animateScroll } from 'react-scroll';

import { ArticleABTestNotification } from '../../components/ArticleABTestNotification';
import { ArticleContent } from '../../components/ArticleContent';
import { ArticleLeftSideBar } from '../../components/ArticleLeftSideBar';
import { ArticlePageLayout } from '../../components/ArticlePageLayout';
import { ArticlePreviewNotification } from '../../components/ArticlePreviewNotification';
import { ArticleRightSideBar } from '../../components/ArticleRightSideBar';
import { ArticleSideBar } from '../../components/ArticleSidebar';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Error } from '../../components/Error';
import { pushUrl } from '../../components/InnerLink';
import {
  LOCALES,
  MOCKED_ROADMAP_LABELS,
  USING_EDITOR_X_SUB_CATEGORY_IDS,
} from '../../constants';
import useIsArticlePageRedesignUnifiedUXEnabled from '../../hooks/useIsArticlePageRedesignUnifiedUXEnabled';
import useIsHelpCenterCopilotEnabled from '../../hooks/useIsHelpCenterCopilotEnabled';
import { useHistoryContext } from '../../hooks/useRouteHistory';
import { useScrollDelegation } from '../../hooks/useScrollDelegation';
import {
  getArticleById,
  getLabels,
  getPageSeo,
} from '../../services/answers';
import { getUserActiveChatbotSession } from '../../services/user';
import { getWixDocsUrl } from '../../services/wix-docs-redirect';
import { GetArticleResponse, PageType, HttpStatusCode } from '../../types';
import { useArticlePageBi } from '../../utils/article-page-bi';
import { mapArticleTypeToItemType } from '../../utils/bi';
import { getCategoriesBreadcrumb } from '../../utils/categories';
import {
  isTwinsRelated,
  isWixelRelated,
} from '../../utils/excluded-categories';
import { isKbServiceEnabled } from '../../utils/experiments';
import { RedirectionError } from '../../utils/extract-http-error-code';
import { fetchArticle } from '../../utils/fetch-article';
import { LoggerNamespaces, createLogger } from '../../utils/logger';
import {
  shouldRedirectArticleToWixDocs,
  getInternalArticleRedirect,
} from '../../utils/redirect-to-wix-docs';
import { getRoadmapLabels, isRoadmapArticle } from '../../utils/roadmap';
import { isSledUserAgent } from '../../utils/user-agent';
import { devFallback, withServerSideProps } from '../../utils/with-server-side-props';
import { useStore } from '../../zustand/stores';
import { CopilotDisplayMode } from '../../zustand/stores/copilot';

import css from './Article.module.scss';

export type Step = {
  id: string;
  text: string | null;
  level: string | null;
};

const getSteps = (article: Article) => {
  if (!article.content) {
    return [];
  }
  const $ = cheerio.load(article.content);
  const newSteps = $('[level="2"],[level="3"],[level="4"]')
    .get()
    .filter((step) => step.attribs.id?.length > 0)
    .map((step) => ({
      id: step.attribs.id,
      text: $(step).text(),
      level: step.attribs.level,
    }));

  return newSteps.length > 1 ? newSteps : [];
};

export type ArticlePageProps = {
  relatedArticles: Article[];
  article: Article;
  roadmapLabels: Label[];
  steps: Step[];
  isLocaleMismatch?: boolean;
  hasActiveChatbotSession?: boolean;
  hasActiveLiveChatSession?: boolean;
};

const ArticlePageContent: NextPage<ArticlePageProps> = ({
  article,
  relatedArticles,
  roadmapLabels,
  steps,
  hasActiveChatbotSession,
  hasActiveLiveChatSession,
}: ArticlePageProps) => {
  const { locale, events } = useRouter();
  const { prePageUrl } = useHistoryContext();
  const itemId = useStore((state) => state.pageSlice.itemId);
  const { article: storeArticle } = useStore((state) => state.articleSlice);
  const categoriesTree = useStore(
    (state) => state.categoriesTreeSlice.categoriesTree
  );
  const {
    copilotDisplayMode,
    setCopilotDisplayMode,
    setIsChatbotInitializing,
    isChatbotReady,
  } = useStore((state) => state.copilotSlice);
  const [showLeftSideBar, setShowLeftSideBar] = useState(true);
  const [showRightSideBar, setShowRightSideBar] = useState(true);
  const [isArticleContentLoading, setIsArticleContentLoading] = useState(false);
  const copilotArticleContentRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const anchorsRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const { push } = useRouter();

  const { breadCrumbLinkClick, homePageClick } = useArticlePageBi(article);

  const showSideBar = article.type === ArticleType.ARTICLE;

  const onBreadCrumbLinkClick = useCallback(
    async (category: Category, clickedItemOrder?: number): Promise<void> =>
      breadCrumbLinkClick(category, clickedItemOrder),
    [breadCrumbLinkClick]
  );

  const isRoadmap =
    article.resolution &&
    article.labels &&
    isRoadmapArticle(article.resolution, article.labels, roadmapLabels);

  const roadmapBreadcrumbs = {
    label: t('roadmap.breadcrumbs'),
    uri: '/roadmap',
  };
  const isPrePageIsRoadmap = prePageUrl?.includes('/roadmap');

  const articleBreadcrumbItem = { id: article.id, label: article.title };
  const categoriesBreadcrumb = getCategoriesBreadcrumb(
    categoriesTree,
    article.categoryId,
    onBreadCrumbLinkClick
  );
  const breadcrumbItems = useMemo(
    () =>
      locale === LOCALES.EN && isRoadmap && isPrePageIsRoadmap
        ? [roadmapBreadcrumbs, articleBreadcrumbItem]
        : [...categoriesBreadcrumb, articleBreadcrumbItem],
    [categoriesTree, article]
  );

  const onHomePageClick = useCallback(
    async (label: string) => {
      if (window) {
        await homePageClick(label);
      }
    },
    [homePageClick]
  );

  const onSelectStatus = async (resolution: ArticleResolution) => {
    await push(`/roadmap?resolution=${resolution}`);
  };
  const selfArticleRedirect = (
    url: string,
    shallow?: boolean,
    targetLocale?: string
  ) => {
    setShowLeftSideBar(false);
    copilotArticleContentRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    animateScroll.scrollToTop({ duration: 400, smooth: true });
    void pushUrl(url, shallow, targetLocale);
    setIsArticleContentLoading(true);
  };

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      setIsArticleContentLoading(false);
    };

    events.on('routeChangeComplete', handleRouteChangeComplete);
    events.on('routeChangeError', handleRouteChangeComplete);

    return () => {
      events.off('routeChangeComplete', handleRouteChangeComplete);
      events.off('routeChangeError', handleRouteChangeComplete);
    };
  }, [events]);

  const isWixelArticle = isWixelRelated(
    PageType.Article,
    categoriesTree,
    itemId,
    storeArticle?.categoryId
  );
  const isTwinsArticle = isTwinsRelated(
    PageType.Article,
    categoriesTree,
    itemId,
    storeArticle?.categoryId
  );
  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();
  const isArticlePageRedesignUnifiedUXEnabled = useIsArticlePageRedesignUnifiedUXEnabled();

  useScrollDelegation(
    copilotArticleContentRef,
    isHelpCenterCopilotEnabled,
    summaryRef,
    anchorsRef
  );

  useEffect(() => {
    if (isHelpCenterCopilotEnabled) {
      if (copilotDisplayMode === CopilotDisplayMode.UnhelpfulFeedback) {
        setCopilotDisplayMode(CopilotDisplayMode.CopilotMainView);
      }
      setShowLeftSideBar(true);
    }
  }, [isHelpCenterCopilotEnabled, article.id]);

  useEffect(() => {
    if (
      !isArticlePageRedesignUnifiedUXEnabled &&
      hasActiveChatbotSession &&
      isHelpCenterCopilotEnabled &&
      !isChatbotReady
    ) {
      setCopilotDisplayMode(CopilotDisplayMode.Loading);
      setIsChatbotInitializing(true);
      setShowRightSideBar(true);
    }
  }, [
    hasActiveChatbotSession,
    isHelpCenterCopilotEnabled,
    isArticlePageRedesignUnifiedUXEnabled,
    setCopilotDisplayMode,
    setIsChatbotInitializing,
    setShowRightSideBar,
    isChatbotReady,
  ]);

  useEffect(() => {
    if (
      isArticlePageRedesignUnifiedUXEnabled &&
      hasActiveLiveChatSession &&
      isHelpCenterCopilotEnabled &&
      !isChatbotReady
    ) {
      setCopilotDisplayMode(CopilotDisplayMode.Loading);
      setIsChatbotInitializing(true);
    }
  }, [
    hasActiveLiveChatSession,
    isHelpCenterCopilotEnabled,
    isArticlePageRedesignUnifiedUXEnabled,
    setCopilotDisplayMode,
    setIsChatbotInitializing,
    isChatbotReady,
  ]);

  if (isArticlePageRedesignUnifiedUXEnabled) {
    return (
      <ArticlePageLayout
        article={article}
        relatedArticles={relatedArticles}
        steps={steps}
        breadcrumbItems={breadcrumbItems}
        copilotArticleContentRef={copilotArticleContentRef}
        anchorsRef={anchorsRef}
        isLoading={isArticleContentLoading}
        selfArticleRedirect={selfArticleRedirect}
        onClickStatus={onSelectStatus}
        hasActiveLiveChatSession={hasActiveLiveChatSession}
      />
    );
  }

  return (
    <>
      <ArticlePreviewNotification />
      <ArticleABTestNotification article={article} />
      <Box
        direction={isHelpCenterCopilotEnabled ? 'horizontal' : 'vertical'}
        className={classNames({
          [css.copilotWrapper]: isHelpCenterCopilotEnabled,
        })}
      >
        {!isHelpCenterCopilotEnabled && (
          <Breadcrumbs
            onHomePageClick={onHomePageClick}
            hideHomePage={isWixelArticle || isTwinsArticle}
            breadcrumbItems={breadcrumbItems}
            shallow={false}
          />
        )}
        {isHelpCenterCopilotEnabled ? (
          <>
            {steps.length > 0 && (
              <ArticleLeftSideBar
                steps={steps}
                article={article}
                showLeftSideBar={showLeftSideBar}
                setShowLeftSideBar={setShowLeftSideBar}
                copilotArticleContentRef={copilotArticleContentRef}
                anchorsRef={anchorsRef}
              />
            )}
            <ArticleContent
              article={article}
              isRoadmapArticle={isRoadmap && locale === LOCALES.EN}
              readingTimeInMinutes={article.readingTimeInMinutes}
              onClickStatus={onSelectStatus}
              relatedArticles={relatedArticles}
              steps={steps}
              breadcrumbItems={breadcrumbItems}
              showLeftSideBar={showLeftSideBar}
              setShowLeftSideBar={setShowLeftSideBar}
              copilotArticleContentRef={copilotArticleContentRef}
              showRightSideBar={showRightSideBar}
              setShowRightSideBar={setShowRightSideBar}
              isLoading={isArticleContentLoading}
              selfArticleRedirect={selfArticleRedirect}
            />
            <ArticleRightSideBar
              showRightSideBar={showRightSideBar}
              setShowRightSideBar={setShowRightSideBar}
              article={article}
              selfArticleRedirect={selfArticleRedirect}
              summaryRef={summaryRef}
              relatedArticles={relatedArticles}
            />
          </>
        ) : (
          <Box
            direction="horizontal"
            width="100%"
            align="center"
            className={css.articleWrapper}
          >
            <ArticleContent
              article={article}
              isRoadmapArticle={isRoadmap && locale === LOCALES.EN}
              readingTimeInMinutes={article.readingTimeInMinutes}
              onClickStatus={onSelectStatus}
              relatedArticles={relatedArticles}
              steps={steps}
              breadcrumbItems={breadcrumbItems}
              showLeftSideBar={showLeftSideBar}
              setShowLeftSideBar={setShowLeftSideBar}
              copilotArticleContentRef={copilotArticleContentRef}
              showRightSideBar={showRightSideBar}
              setShowRightSideBar={setShowRightSideBar}
              isLoading={false}
              selfArticleRedirect={selfArticleRedirect}
            />
            {showSideBar && (
              <Box
                direction="vertical"
                position="relative"
                className={css.rightSideNew}
              >
                <ArticleSideBar article={article} steps={steps} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

const ArticlePage: NextPage<ArticlePageProps> = (props) =>
  props.isLocaleMismatch ? (
    <Error
      statusCode={HttpStatusCode.NOT_FOUND}
      englishArticleUri={`/article${props.article.uri}`}
    />
  ) : (
    <ArticlePageContent {...props} />
  );

export const getServerSideProps = withServerSideProps(
  async ({ req, res, locale, query, experiments, isWixEmployee, isLoggedInUser }) => {
    const logger = createLogger(LoggerNamespaces.Article);
    const { articleTitle, alternativeContent, preview } = query;
    const withAlternativeContent = alternativeContent === 'true';
    const useV2 = isKbServiceEnabled(experiments);
    let article: Pick<GetArticleResponse, 'article'>['article'];
    let relatedArticles: Pick<
      GetArticleResponse,
      'relatedArticles'
    >['relatedArticles'];
    let isFromV2 = false;
    const shouldConductVersionB = withAlternativeContent && isWixEmployee;

    const isPreview = preview === 'true';
    try {
      const data = await fetchArticle({
        useV2,
        aspects: req.aspects,
        appContext: res.locals.appContext,
        redisClient: res.locals.redisClient,
        uri: articleTitle as string,
        locale,
        isPreview,
        isWixEmployee,
        shouldConductVersionB,
      });

      article = data.article;
      relatedArticles = data.relatedArticles;
      isFromV2 = data.isFromV2 ?? false;
    } catch (error: any) {
      if (error instanceof RedirectionError) {
        return {
          redirect: {
            statusCode: HttpStatusCode.MOVED_PERMANENTLY,
            destination: encodeURI(`/${locale}/article${error.location}`),
          },
        };
      }
      throw error;
    }

    if (article.locale !== locale) {
      res.statusCode = HttpStatusCode.NOT_FOUND;
      return {
        props: {
          article,
          isLocaleMismatch: true,
        },
      };
    }

    let roadmapLabels: Label[] = [];
    if (article.type === ArticleType.FEATURE_REQUEST) {
      const allLabels = isProduction()
        ? await getLabels(req.aspects, res.locals.redisClient)
        : MOCKED_ROADMAP_LABELS;
      roadmapLabels = getRoadmapLabels(allLabels as Label[]);
    }

    if (USING_EDITOR_X_SUB_CATEGORY_IDS.includes(article.categoryId || '')) {
      // editor x is deprecated. Redirect to migration article.
      logger.info('Diagnostic of redirections of old EditorX articles', {
        articleUri: articleTitle,
      });
      return {
        redirect: {
          statusCode: 301,
          destination: encodeURI(
            `/${locale}/article/wix-studio-faqs-transitioning-from-editor-x`
          ),
        },
      };
    }

    const internalRedirectUrl = getInternalArticleRedirect(article.id);
    if (internalRedirectUrl) {
      return {
        redirect: {
          statusCode: HttpStatusCode.MOVED_PERMANENTLY,
          destination: encodeURI(`/${locale}/article/${internalRedirectUrl}`),
        },
      };
    }

    if (shouldRedirectArticleToWixDocs(article.id)) {
      const isEnglish = article.locale === LOCALES.EN;

      const redirectArticle = !isEnglish
        ? await getArticleById(
          req.aspects,
          res.locals.appContext,
          article.id as string,
          LOCALES.EN,
          useV2
        )
        : article;

      const wixDocsUrl = await getWixDocsUrl(
        res.locals.appContext,
        redirectArticle.url as string
      );

      if (wixDocsUrl) {
        const userAgent = req.headers['user-agent'];
        if (isProduction() && userAgent && !isSledUserAgent(userAgent)) {
          const logData = {
            from: redirectArticle.url,
            to: wixDocsUrl,
            referrer: req.headers.referer,
          };
          logger.info('Redirecting to Wix Docs', logData);
        }

        return {
          redirect: {
            statusCode: HttpStatusCode.MOVED_PERMANENTLY,
            destination: wixDocsUrl,
          },
        };
      }
    }

    const useStaticSeo = isKbServiceEnabled(experiments);
    const [pageSeo, activeSession] = await Promise.all([
      useStaticSeo
        ? Promise.resolve(null)
        : getPageSeo(
          req.aspects,
          locale,
          PageType.Article,
          res.locals.redisClient
        ).catch(devFallback(null)),
      isLoggedInUser
        ? getUserActiveChatbotSession(req.aspects, res.locals.appContext)
        : Promise.resolve<GetActiveSessionResponse>({}),
    ]);

    const hasActiveChatbotSession = !!activeSession?.lastMessageId;
    const hasActiveLiveChatSession = !!activeSession?.liveChat;

    return {
      props: {
        relatedArticles,
        article,
        hasActiveChatbotSession,
        hasActiveLiveChatSession,
        pageData: {
          article: {
            ...article,
            withRelatedArticles: !!relatedArticles && relatedArticles.length,
            isFromV2,
          },
          roadmapData: { roadmapLabels },
        },
        pageType: PageType.Article,
        itemId: article.id,
        itemType: mapArticleTypeToItemType(article?.type),
        pageSeo,
        roadmapLabels,
        steps: getSteps(article as Article),
      },
    };
  }
);

export default ArticlePage;
